'use client';

/**
 * SkipToContent — Phase E Sprint 17 a11y primitive.
 *
 * A visually-hidden skip-link rendered at the very top of the document
 * that becomes visible on focus. Keyboard users (and SR users tabbing
 * through the page) hit Tab once after page-load and get a one-press
 * shortcut into the main content region, bypassing the nav sidebar +
 * top bar.
 *
 * Standard WCAG 2.4.1 "Bypass blocks" technique. Wire as the FIRST
 * focusable element inside the dashboard shell + ensure the target
 * element has a stable id (default `#main-content`).
 *
 * Usage:
 *   <body>
 *     <SkipToContent />
 *     <header>...</header>
 *     <main id="main-content">...</main>
 *   </body>
 *
 * Or with a custom target:
 *   <SkipToContent targetId="dashboard-main" label="Skip to dashboard" />
 *   <main id="dashboard-main">...</main>
 */

import React from 'react';
import { cn } from '../../lib/utils';

export interface SkipToContentProps {
  /**
   * The id of the element to skip to. Defaults to `main-content` which
   * is the dashboard shell's standard main-region id.
   */
  targetId?: string;
  /** Label shown when the link is focused. Defaults to "Skip to main content". */
  label?: string;
  className?: string;
}

export function SkipToContent({
  targetId = 'main-content',
  label = 'Skip to main content',
  className,
}: SkipToContentProps) {
  return (
    <a
      href={`#${targetId}`}
      className={cn(
        // Visually-hidden until focused (sr-only equivalent without depending
        // on Tailwind's sr-only being available in every consumer).
        'absolute left-2 top-2 z-50 -translate-y-16 rounded-md bg-statura-green-accessible px-3 py-2 text-sm font-semibold text-white shadow-md',
        'transition-transform focus:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-ink',
        className,
      )}
      data-skip-to-content
    >
      {label}
    </a>
  );
}
